import React, { useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { RATING_CHIP_STYLES, formatRawValue, getThresholdNote, deriveRatingFromRaw } from '../constants';
import RatingCriteriaPopover, { RatingCriteriaButton } from './RatingCriteriaPopover';

/**
 * Post-parse evidence block.
 *
 * Shows the rating that came back from the document parse, the raw figures
 * that drove it, and which threshold rule classified it. Lets the user
 * verify the inference before any score is computed.
 *
 * Visible when the variable has either:
 *   - a `rating` (Gemini classified it) and/or
 *   - a `raw` payload (Gemini gave numbers; rating may be null and will
 *     compute server-side at /score time)
 *
 * If the user later overrides the rating from the dropdown above, the
 * document stays attached and a "Use doc rating" link reverts.
 */
export default function ParseResultBlock({ variable, value, onRevertOverride }) {
    const criteriaButtonRef = useRef(null);
    const [showCriteria, setShowCriteria] = useState(false);

    const explicitRating = value?.rating;
    const raw = value?.raw;
    const provenance = value?.provenance;
    if (!explicitRating && !raw) return null;

    // If Gemini gave us numbers but no rating, derive it client-side using the
    // same rules the calculator uses server-side. Marks the chip as "computed"
    // so the user knows it came from our threshold logic, not the document
    // saying "Strong" verbatim.
    const derivedRating = !explicitRating && raw ? deriveRatingFromRaw(variable.key, raw) : null;
    const rating = explicitRating || derivedRating;

    const isDocRating = provenance === 'doc_verified';
    const isProxy     = provenance === 'proxy';
    const isOverride  = provenance === 'self_selected' && !!value?.source_document;
    const chipClass   = rating ? (RATING_CHIP_STYLES[rating] || 'bg-slate-100 text-slate-800') : '';
    const formattedRaw = formatRawValue(variable.key, raw);
    const threshold    = getThresholdNote(variable.key, raw);

    const heading = isOverride       ? 'Override rating'
                  : isDocRating      ? 'Rating from document'
                  : isProxy          ? 'From baseline data'
                  : 'Rating';

    return (
        <div className="mt-3 p-3 rounded-md bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                        {heading}
                    </span>
                    {rating ? (
                        <>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${chipClass}`}>
                                {rating}
                            </span>
                            {derivedRating && !explicitRating && (
                                <span className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                                    computed from figures
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400 italic">
                            Pending — will compute on Calculate
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <RatingCriteriaButton buttonRef={criteriaButtonRef} onClick={() => setShowCriteria(v => !v)} />
                    {isOverride && onRevertOverride && (
                        <button onClick={onRevertOverride}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors"
                                title="Revert to document rating">
                            <RotateCcw size={11} /> Use doc rating
                        </button>
                    )}
                </div>
            </div>

            {formattedRaw && (
                <div className="mt-1.5 text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">
                    Based on: {formattedRaw}
                </div>
            )}
            {threshold && (
                <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                    {threshold}
                </div>
            )}

            <RatingCriteriaPopover
                variableKey={variable.key}
                label={variable.label}
                anchorRef={criteriaButtonRef}
                open={showCriteria}
                onClose={() => setShowCriteria(false)}
            />
        </div>
    );
}
